<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Button,
		ButtonV2,
		Checkbox,
		Container,
		InputV2,
		Modal,
		Text,
		Toggle,
		ToggleV2,
		toast,
	} from '@Shelly/shelly-design-system';
	import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
	import {
		getBluTRVGatewayFirmwareVersion,
		getTRVFirmwareVersion,
	} from 'src/helpers/firmware_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		BLU_TRV_MIN_VALVE_MAX,
		BLU_TRV_MIN_VALVE_MIN,
		bluTrvHasMinValve,
		buildBluTrvSetConfigSlice,
	} from 'src/helpers/settings/blu_trv_settings_helpers';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		callBluTRV,
		getTrvComponentByDevice,
		isBluTRVCalibrated,
	} from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		consumeSettingsRouteIntent,
		settingsRouteIntent,
	} from 'src/stores/ui/route-to-device-settings';
	import SettingGuide from 'src/components/DevicesSettings/SettingGuide.svelte';

	export let device: Device;
	let waitForResponse = false;
	let waitForEnableResponse = false;
	let waitForCalibrate = false;

	type Flags =
		| 'floor_heating'
		| 'accel'
		| 'auto_calibrate'
		| 'anticlog'
		| 'power_save'
		| 'silent_mode';

	type EnabledFlags = Flags[];

	const trvConfig = getTrvComponentByDevice(device)?.config?.['trv:0'];
	let enabledFlags: EnabledFlags = (trvConfig?.flags || []) as EnabledFlags;
	let enableTrv = trvConfig?.enable;
	const hasMinValve = bluTrvHasMinValve(trvConfig);
	let minValve: number | string = trvConfig?.min_valve_position ?? BLU_TRV_MIN_VALVE_MIN;

	const isPowerBalancedHeatingSupport = compareFWVersions(
		getTRVFirmwareVersion(device),
		'>=',
		'1.2.0',
	);
	const isSilentModeSupport =
		compareFWVersions(getTRVFirmwareVersion(device), '>=', '1.3.0') &&
		compareFWVersions(getBluTRVGatewayFirmwareVersion(device), '>=', '2.0.0');
	const powerSaveKey = 'power_save';
	const flagCards: {
		flag: Flags;
		titleKey: string;
		titleDefault: string;
		infoKey: string;
		infoDefault: string;
		id: string;
	}[] = [
		{
			flag: 'floor_heating',
			titleKey: 'checkbox_blu_trv_behavior_floor_heating',
			titleDefault: 'External temperature control',
			infoKey: 'checkbox_info_blu_trv_behavior_floor_heating',
			infoDefault: 'Only data from the external sensor is used, ignoring the built-in sensor',
			id: 'toggle-blu-trv-floor-heating',
		},
		{
			flag: 'accel',
			titleKey: 'checkbox_blu_trv_behavior_accel',
			titleDefault: 'Accelerated heating',
			infoKey: 'checkbox_info_blu_trv_behavior_accel',
			infoDefault: 'Reduce the time needed to reach the target temperature',
			id: 'toggle-blu-trv-accel',
		},
		{
			flag: 'auto_calibrate',
			titleKey: 'checkbox_blu_trv_behavior_auto_calibrate',
			titleDefault: 'Auto calibration',
			infoKey: 'checkbox_info_blu_trv_behavior_auto_calibrate',
			infoDefault: 'The TRV will automatically correct the calibration if needed',
			id: 'toggle-blu-trv-auto-calibrate',
		},
		{
			flag: 'anticlog',
			titleKey: 'checkbox_blu_trv_behavior_anticlog',
			titleDefault: 'Clog prevention',
			infoKey: 'checkbox_info_blu_trv_behavior_anticlog',
			infoDefault:
				'If enabled, the TRV will ensure at least one movement per week to prevent clogging',
			id: 'toggle-blu-trv-anticlog',
		},
		{
			flag: 'power_save',
			titleKey: 'checkbox_blu_trv_balanced_heating',
			titleDefault: 'Balanced heating',
			infoKey: 'checkbox_info_blu_trv_balanced_heating',
			infoDefault: 'If enabled, balances power-saving and precision; otherwise, favors precision.',
			id: 'toggle-blu-trv-balanced-heating',
		},
		{
			flag: 'silent_mode',
			titleKey: 'checkbox_blu_trv_behavior_silent_mode',
			titleDefault: 'Silent mode',
			infoKey: 'checkbox_info_blu_trv_behavior_silent_mode',
			infoDefault: 'Reduce noise during valve adjustments.',
			id: 'toggle-blu-trv-silent-mode',
		},
	];
	$: visibleFlagCards = flagCards.filter((card) => {
		if (card.flag === 'power_save') return isPowerBalancedHeatingSupport;
		if (card.flag === 'silent_mode') return isSilentModeSupport;
		return true;
	});
	let init_power_save = enabledFlags.includes(powerSaveKey);
	let showBalancedHeatingPrompt = false;
	let lastSavedEnable = enableTrv;
	let lastSavedFlags: EnabledFlags = [...enabledFlags];
	let lastSavedMinValve = Number(minValve);

	$: numericMinValve = Number(minValve);
	$: minValveInfo = getErrorOrInfoMessageForMinAndMax(
		numericMinValve,
		BLU_TRV_MIN_VALVE_MIN,
		BLU_TRV_MIN_VALVE_MAX,
	);
	$: flagsDirty = [...enabledFlags].sort().join() !== [...lastSavedFlags].sort().join();
	$: enableDirty = enableTrv !== lastSavedEnable;
	$: minValveDirty = hasMinValve && numericMinValve !== lastSavedMinValve;
	$: minValveInvalid = hasMinValve && !!minValveInfo.error;
	$: isDirty = flagsDirty || enableDirty || minValveDirty;
	$: saveDisabled = waitForResponse || !isDirty || (minValveDirty && minValveInvalid);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: requestSave,
				loading: waitForResponse,
				disabled: saveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	const toFlagsObject = (enabled: EnabledFlags): Record<Flags, boolean> => {
		const keys: Flags[] = ['floor_heating', 'accel', 'auto_calibrate', 'anticlog'];
		if (isPowerBalancedHeatingSupport) keys.push('power_save');
		if (isSilentModeSupport) keys.push('silent_mode');
		return keys.reduce(
			(acc, key) => {
				acc[key] = enabled.includes(key);
				return acc;
			},
			{} as Record<Flags, boolean>,
		);
	};

	const handleEnableTrv = async () => {
		waitForEnableResponse = true;
		enableTrv = !enableTrv;
		await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config: { enable: enableTrv },
		});
		lastSavedEnable = enableTrv;
		waitForEnableResponse = false;
	};

	const handleBehaviour = (flag: Flags): void => {
		if (enabledFlags.includes(flag)) {
			enabledFlags = enabledFlags.filter((item) => item !== flag);
		} else {
			enabledFlags = [...enabledFlags, flag];
		}
	};

	const behaviour = async (): Promise<void> => {
		waitForResponse = true;
		const minValveSlice = buildBluTrvSetConfigSlice({
			min_valve_position: { dirty: minValveDirty, value: Math.round(numericMinValve) },
		});
		const res: CloudApiResponse = await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config: $showNewLayout
				? { enable: enableTrv, flags: toFlagsObject(enabledFlags), ...minValveSlice }
				: { flags: enabledFlags, ...minValveSlice },
		});
		if (res.isok) {
			init_power_save = enabledFlags.includes(powerSaveKey);
			lastSavedFlags = [...enabledFlags];
			if ($showNewLayout) lastSavedEnable = enableTrv;
			if (minValveDirty) {
				lastSavedMinValve = Math.round(numericMinValve);
				minValve = lastSavedMinValve;
			}
		}
		waitForResponse = false;
	};

	const requestSave = () => {
		if ($showNewLayout && saveDisabled) return;
		if (isPowerBalancedHeatingSupport && enabledFlags.includes(powerSaveKey) != init_power_save) {
			showBalancedHeatingPrompt = true;
		} else {
			behaviour();
		}
	};

	const calibrate = async (): Promise<void> => {
		waitForCalibrate = true;
		await callBluTRV(device, 'Trv.Calibrate', { id: 0 });
		waitForCalibrate = false;
	};

	$: if ($settingsRouteIntent?.initiate) {
		consumeSettingsRouteIntent();
		void calibrate();
	}

	const isNotCalibrated: boolean = !isBluTRVCalibrated(device);
	let checkingCalibrated = isNotCalibrated;

	$: if (checkingCalibrated && isBluTRVCalibrated(device)) {
		checkingCalibrated = false;
		toast.open({
			type: 'success',
			title: $format('text_blu_trv_calibrate_successful', {
				default: 'Calibration is successful',
			}),
		});
	}
</script>

{#if $showNewLayout}
	<div class="blu-trv-behavior">
		<SettingGuide
			text={$format('settings_guide_trv_behavior', {
				default:
					'Calibrate the valve, then choose heating modes and the minimum opening under automatic temperature control.',
			})}
		/>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{capFirstChar(
					$format('sub_settings_title_calibration', {
						default: 'Calibration',
					}),
				)}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_trv_calibration', {
					default:
						'Run a one-time valve calibration. This can take up to 10 seconds, and the TRV ignores other commands until it finishes.',
				})}
			</Text>
			<ButtonV2
				size="m"
				width="full"
				loading={waitForCalibrate}
				disabled={waitForCalibrate}
				on:click={calibrate}
			>
				{$format('button_start_calibration', {
					default: '"Start calibration"',
				})}
			</ButtonV2>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_auto_control', {
					default: 'Automatic temperature control',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('blutrv_enable_warning_message_description', {
					default:
						'If disabled, the TRV would not open/close the valve automatically, which is suitable only if using advanced API calls to control the valve position manually.',
				})}
			</Text>
			<ToggleV2 size="m" class="mb-s" bind:checked={enableTrv} id="toggle-trv-enable">
				{capFirstChar($format('enable', { default: 'Enable' }))}
			</ToggleV2>

			{#if !enableTrv}
				<TransitionWrapper>
					<Alert
						class="mb-s"
						type="info"
						heading={capFirstChar(
							$format('alert_info_heading_trv_behavior_disabled', {
								default: 'Behavior settings disabled',
							}),
						)}
						isHeadingBold
						message={$format('alert_info_message_trv_behavior_disabled', {
							default:
								'The behavior settings below have no effect while automatic temperature control is disabled. Enable automatic temperature control to configure these settings.',
						})}
					/>
				</TransitionWrapper>
			{/if}

			{#each visibleFlagCards as card (card.flag)}
				<div class="mb-l">
					<Text element="h2" styleType="h2" class="mb-xs">
						{$format(card.titleKey, { default: card.titleDefault })}
					</Text>
					<Text styleType="body-small" class="mb-s text-color-muted">
						{$format(card.infoKey, { default: card.infoDefault })}
					</Text>
					<ToggleV2
						size="m"
						checked={enabledFlags.includes(card.flag)}
						disabled={!enableTrv}
						on:click={() => handleBehaviour(card.flag)}
						id={card.id}
					>
						{capFirstChar($format('enable', { default: 'Enable' }))}
					</ToggleV2>
				</div>
			{/each}

			{#if hasMinValve}
				<div>
					<Text element="h2" styleType="h2" class="mb-xs">
						{$format('settings_heading_trv_min_valve', {
							default: 'Minimum valve position',
						})}
					</Text>
					<Text styleType="body-small" class="mb-s text-color-muted">
						{$format('min_valve_description', {
							default: 'Set minimum position of valve ({min}% - {max}%)',
							values: { min: BLU_TRV_MIN_VALVE_MIN, max: BLU_TRV_MIN_VALVE_MAX },
						})}
					</Text>
					<InputV2
						aria-label={$format('input_blu_trv_min_valve', {
							default:
								'Enter the minimum valve position. Value must be between {min} — {max}!',
							values: { min: BLU_TRV_MIN_VALVE_MIN, max: BLU_TRV_MIN_VALVE_MAX },
						})}
						{...getNumberInputConfig(true)}
						icon={null}
						size="m"
						width="full"
						disabled={!enableTrv}
						info={minValveInfo.info}
						error={minValveInfo.error}
						bind:value={minValve}
						id="input-blu-trv-min-valve"
					/>
				</div>
			{/if}
		</Container>
	</div>
{:else}
	<div class="blu-trv-behavior">
		<Alert
			class="mb-s"
			type="warning"
			heading={capFirstChar(
				$format('alert_warning_generic_attention', {
					default: 'Attention!',
				}),
			)}
			dismissable
			isHeadingBold
			message={$format('blutrv_enable_warning_message_description', {
				default:
					'If disabled, the TRV would not open/close the valve automatically, which is suitable only if using advanced API calls to control the valve position manually.',
			})}
		/>

		<Toggle
			loading={waitForEnableResponse}
			disabled={waitForEnableResponse}
			checked={enableTrv}
			size="m"
			class="mb-m"
			on:click={handleEnableTrv}
			id="toggle-trv-enable"
		>
			{$format('trv_auto_description', {
				default: 'Enable automatic temperature control',
			})}
		</Toggle>

		{#if !enableTrv}
			<TransitionWrapper>
				<Alert
					class="mb-s"
					type="info"
					heading={capFirstChar(
						$format('alert_info_heading_trv_behavior_disabled', {
							default: 'Behavior settings disabled',
						}),
					)}
					isHeadingBold
					message={$format('alert_info_message_trv_behavior_disabled', {
						default:
							'The behavior settings below have no effect while automatic temperature control is disabled. Enable automatic temperature control to configure these settings.',
					})}
				/>
			</TransitionWrapper>
		{/if}

		<Checkbox
			size="m"
			class="flex-shrink-0 mr-s mb-s"
			checked={enabledFlags.includes('floor_heating')}
			disabled={!enableTrv}
			info={$format('checkbox_info_blu_trv_behavior_floor_heating', {
				default: 'Only data from the external sensor is used, ignoring the built-in sensor',
			})}
			on:click={() => handleBehaviour('floor_heating')}
			id="checkbox-at4icyn"
		>
			{$format('checkbox_blu_trv_behavior_floor_heating', {
				default: 'Underfloor heating',
			})}
		</Checkbox>
		<Checkbox
			size="m"
			class="flex-shrink-0 mr-s mb-s"
			checked={enabledFlags.includes('accel')}
			disabled={!enableTrv}
			info={$format('checkbox_info_blu_trv_behavior_accel', {
				default: 'Reduce the time needed to reach the target temperature',
			})}
			on:click={() => handleBehaviour('accel')}
			id="checkbox-yhe15nj"
		>
			{$format('checkbox_blu_trv_behavior_accel', {
				default: 'Accelerated heating',
			})}
		</Checkbox>
		<Checkbox
			size="m"
			class="flex-shrink-0 mr-s mb-s"
			checked={enabledFlags.includes('auto_calibrate')}
			disabled={!enableTrv}
			info={$format('checkbox_info_blu_trv_behavior_auto_calibrate', {
				default: 'The TRV will automatically correct the calibration if needed',
			})}
			on:click={() => handleBehaviour('auto_calibrate')}
			id="checkbox-6fcmvz4"
		>
			{$format('checkbox_blu_trv_behavior_auto_calibrate', {
				default: 'Auto calibration',
			})}
		</Checkbox>
		<Checkbox
			size="m"
			class="flex-shrink-0 mr-s mb-s"
			checked={enabledFlags.includes('anticlog')}
			disabled={!enableTrv}
			info={$format('checkbox_info_blu_trv_behavior_anticlog', {
				default:
					'If enabled, the TRV will ensure at least one movement per week to prevent clogging',
			})}
			on:click={() => handleBehaviour('anticlog')}
			id="checkbox-2nvyipx"
		>
			{$format('checkbox_blu_trv_behavior_anticlog', {
				default: 'Clog prevention',
			})}
		</Checkbox>
		{#if isPowerBalancedHeatingSupport}
			<Checkbox
				size="m"
				class="flex-shrink-0 mr-s mb-s"
				checked={enabledFlags.includes(powerSaveKey)}
				disabled={!enableTrv}
				info={$format('checkbox_info_blu_trv_balanced_heating', {
					default:
						'If enabled, balances power-saving and precision; otherwise, favors precision.',
				})}
				on:click={() => handleBehaviour(powerSaveKey)}
				id="checkbox-o84r4v0"
			>
				{$format('checkbox_blu_trv_balanced_heating', {
					default: 'Balanced heating',
				})}
			</Checkbox>
		{/if}
		{#if isSilentModeSupport}
			<Checkbox
				size="m"
				class="flex-shrink-0 mr-s mb-s"
				checked={enabledFlags.includes('silent_mode')}
				disabled={!enableTrv}
				info={$format('checkbox_info_blu_trv_behavior_silent_mode', {
					default: 'Reduce noise during valve adjustments.',
				})}
				on:click={() => handleBehaviour('silent_mode')}
				id="checkbox-blu-trv-silent-mode"
			>
				{$format('checkbox_blu_trv_behavior_silent_mode', {
					default: 'Silent mode',
				})}
			</Checkbox>
		{/if}

		{#if hasMinValve}
			<Text element="h2" styleType="h2" class="mb-xs">
				{$format('settings_heading_trv_min_valve', {
					default: 'Minimum valve position',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('min_valve_description', {
					default: 'Set minimum position of valve ({min}% - {max}%)',
					values: { min: BLU_TRV_MIN_VALVE_MIN, max: BLU_TRV_MIN_VALVE_MAX },
				})}
			</Text>
			<InputV2
				aria-label={$format('input_blu_trv_min_valve', {
					default: 'Enter the minimum valve position. Value must be between {min} — {max}!',
					values: { min: BLU_TRV_MIN_VALVE_MIN, max: BLU_TRV_MIN_VALVE_MAX },
				})}
				{...getNumberInputConfig(true)}
				icon={null}
				size="m"
				width="full"
				class="mb-s"
				disabled={!enableTrv}
				info={minValveInfo.info}
				error={minValveInfo.error}
				bind:value={minValve}
				id="input-blu-trv-min-valve-legacy"
			/>
		{/if}

		<Button
			loading={waitForResponse}
			disabled={waitForResponse || !enableTrv || (minValveDirty && minValveInvalid)}
			class="mt-s"
			size="m"
			width="full"
			on:click={requestSave}
			>{$format('button_save_settings', {
				default: 'Save settings',
			})}</Button
		>
	</div>
{/if}

<Modal
	on:close={() => {
		showBalancedHeatingPrompt = false;
	}}
	showModal={showBalancedHeatingPrompt}
	title={$format('label_exit_modal', {
		default: 'Exit Without Saving?',
	})}
	backdropClose={false}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('blu_trv_prompt_balanced_heating_info', {
				default:
					'Toggling Balanced heating resets algorithm settings and the BLU TRV may take some time to adapt to your heating patterns. Apply the change?',
			})}
		</Text>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			width="m"
			variant="secondary"
			class="mr-s"
			on:click={() => {
				showBalancedHeatingPrompt = false;
			}}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			width="m"
			on:click={() => {
				showBalancedHeatingPrompt = false;
				behaviour();
			}}
		>
			{capFirstChar($format('button_apply', { default: 'Apply' }))}
		</Button>
	</div>
</Modal>

<style>
	.blu-trv-behavior {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
